/**
 * Changing a page's type without losing what is on it.
 *
 * Every structured page is "a title, a line of context, and a list of points" dressed differently: columns,
 * boxes, timeline steps, funnel stages, metrics, bullets. retype() starts from the catalogue's blank page for
 * the new type and pours the current page into it — the common fields as they are, and the list translated
 * into the new page's list shape (a column with bullets becomes an arrow point with a multi-line body; a metric
 * becomes a share column with its value as the big figure; steps keep their labels).
 */
import { LAYOUT_CATALOG, type LayoutEntry } from './layouts';
import { splitLead } from '@/cowork/omniad/components/slides/SlideRenderer';

type Slide = Record<string, any>;

/** Fields that mean the same on every page type. */
export function pickCommon(s: Slide): Partial<Slide> {
  const out: Partial<Slide> = {};
  for (const k of ['eyebrow', 'title', 'kicker', 'theme', 'notes', 'media', 'kaiHint', 'aside', 'callout', 'footnote']) if (s[k] !== undefined) out[k] = s[k];
  return out;
}

/** The catalogue entry a page most resembles: same type, then the closest variant / layout / structure. */
export function matchLayout(s: Slide): LayoutEntry | undefined {
  const type = s.type || 'content';
  let best: LayoutEntry | undefined; let bestScore = -1;
  for (const l of LAYOUT_CATALOG) {
    const m = l.make();
    if ((m.type || 'content') !== type) continue;
    let score = 0;
    if ((m.variant || '') === (s.variant || '')) score += 4;
    if ((m.layout || '') === (s.layout || '')) score += 2;
    if (!!m.bullets === !!s.bullets) score += 1;
    if (m.chart && s.chart && m.chart.type === s.chart.type) score += 3;
    if (Array.isArray(m.columns) && Array.isArray(s.columns) && m.columns.length === s.columns.length) score += 1;
    if (score > bestScore) { best = l; bestScore = score; }
  }
  return best;
}

/** One point of a page's list, in a shape every list type can be read into and written from. */
interface Point { title?: string; body?: string; label?: string; icon?: string; value?: string; bullets?: string[]; media?: unknown; raw: Slide }

const LIST_KEYS = ['columns', 'items', 'steps', 'metrics', 'bullets'] as const;
type ListKey = typeof LIST_KEYS[number];

const listKeyOf = (s: Slide): ListKey | undefined => LIST_KEYS.find((k) => Array.isArray(s[k]) && s[k].length > 0);

/** Read a page's list as points. Showcase pages hold creator profiles, which are not points. */
export function pointsOf(s: Slide): Point[] {
  if (s.type === 'showcase') return [];
  const key = listKeyOf(s); if (!key) return [];
  return (s[key] as unknown[]).map((it): Point => {
    if (typeof it === 'string') { const lead = splitLead(it); return lead ? { title: lead[0], body: lead[1], raw: {} } : { body: it, raw: {} }; }
    const o = (it || {}) as Slide;
    // A column's eyebrow is either a figure (share columns: "70%") or a small label ("THREE ASSETS").
    const figure = typeof o.eyebrow === 'string' && /^[€$£+\-~]?\d/.test(o.eyebrow.trim()) ? o.eyebrow : undefined;
    return {
      title: o.title || o.from || (key === 'metrics' ? o.label : undefined),
      body: o.body || o.caption || (o.from ? o.to : undefined),
      label: key === 'metrics' ? undefined : (o.label || (figure ? undefined : o.eyebrow) || o.subtitle),
      icon: o.icon,
      value: o.value || figure,
      bullets: Array.isArray(o.bullets) && o.bullets.length ? o.bullets : undefined,
      media: o.media,
      raw: o,
    };
  }).filter((p) => p.title || p.body || p.value || p.bullets);
}

const clean = <T extends Slide>(o: T): T => { for (const k of Object.keys(o)) if (o[k] === undefined) delete o[k]; return o; };

/** Write points into the new page's list, item by item over the template's item shape. */
function fillList(m: Slide, pts: Point[], sameKey: boolean): Slide | null {
  if (m.type === 'showcase' || !pts.length) return null;
  const key = listKeyOf(m); if (!key) return null;
  const tmpl = m[key] as Slide[];
  if (key === 'bullets') {
    const lines: string[] = [];
    for (const p of pts) {
      const head = p.title && p.body ? `**${p.title}** — ${p.body}` : p.value && p.title ? `**${p.value}** — ${p.title}` : p.title && p.bullets ? `**${p.title}**` : p.title || p.body;
      if (head) lines.push(head);
      if (p.bullets) lines.push(...p.bullets);
    }
    return lines.length ? { [key]: lines } : null;
  }
  const fixed = m.type === 'comparison' || m.variant === 'petals';
  const n = fixed ? tmpl.length : Math.max(1, Math.min(pts.length, 8));
  const list = Array.from({ length: n }, (_, i) => {
    const t = tmpl[Math.min(i, tmpl.length - 1)] || {}; const p = pts[i];
    if (!p) return t;
    const body = p.body || (p.bullets ? p.bullets.join('\n') : undefined);
    const item: Slide = { ...t, ...(sameKey ? p.raw : {}), title: p.title, body, icon: p.icon ?? t.icon, media: p.media ?? t.media };
    switch (key) {
      case 'columns':
        item.eyebrow = p.value || p.label || t.eyebrow;
        if (p.bullets && !p.body) { item.bullets = p.bullets; delete item.body; }
        break;
      case 'steps':
        item.label = p.label || p.value || t.label;
        break;
      case 'metrics':
        item.value = p.value || t.value; item.label = p.title || p.label || t.label; item.caption = body;
        delete item.title; delete item.body;
        break;
      case 'items':
        if (m.variant === 'transition') { item.from = p.title || p.body; item.to = p.title ? p.body : undefined; delete item.title; delete item.body; }
        else if (m.type === 'comparison') { item.label = p.title || p.label || t.label; item.value = p.value || t.value; }
        else {
          if (t.label !== undefined) item.label = p.label || t.label;
          // A figure with nowhere to go joins the title ("3.1M — Impressions").
          if (p.value && t.value === undefined && t.eyebrow === undefined) item.title = [p.value, p.title].filter(Boolean).join(' — ');
        }
        break;
    }
    return clean(item);
  });
  return { [key]: list };
}

/**
 * Change a page's type: the catalogue's blank page for the new type, plus everything from the current page that
 * the new one has a slot for — common fields, same-shaped fields (a chart keeps its data and only changes style),
 * and the list translated into the new list shape.
 */
export function retype(s: Slide, m: Slide): Slide {
  const out: Slide = { ...m, ...pickCommon(s) };
  for (const k of Object.keys(m)) {
    if (['type', 'variant', 'layout', 'theme', 'chart'].includes(k) || s[k] === undefined) continue;
    if (typeof s[k] === typeof m[k] && Array.isArray(s[k]) === Array.isArray(m[k])) out[k] = s[k];
  }
  if (m.chart && s.chart && typeof s.chart === 'object') {
    // A side-panel layout (chart + text / image) is about the page, not the chart: the chart keeps its style.
    const panel = ['text-left', 'text-right', 'image-left', 'image-right'].includes(m.layout);
    out.chart = { ...m.chart, ...s.chart, type: (panel && s.chart.type) || m.chart.type || s.chart.type };
  }
  const filled = fillList(m, pointsOf(s), listKeyOf(s) === listKeyOf(m));
  if (filled) Object.assign(out, filled);
  // A body with no slot on the new page still has a home as the kicker line, and vice versa.
  if (typeof s.body === 'string' && s.body && out.body === undefined && typeof m.kicker === 'string' && !out.kicker) out.kicker = s.body;
  out.type = m.type; out.theme = m.theme || s.theme;
  return clean(out);
}
